/**
 * Outcome Tracking enum mappings and utilities
 * Centralized mappings for salon configuration tracking options
 */

import { OutcomeTrackingMode } from "@salon/types";
import type { TFunction } from "i18next";
import { CheckCircle, Clock } from "lucide-react";

export interface EnumOption<T> {
  value: T;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge: string;
}

export const OUTCOME_TRACKING_MODE_OPTIONS: EnumOption<OutcomeTrackingMode>[] = [
  {
    value: OutcomeTrackingMode.AUTO,
    label: "settings:salonConfig.trackingModes.auto.label",
    description: "settings:salonConfig.trackingModes.auto.description",
    icon: CheckCircle,
    badge: "settings:salonConfig.trackingModes.auto.badge",
  },
  {
    value: OutcomeTrackingMode.MANUAL,
    label: "settings:salonConfig.trackingModes.manual.label",
    description: "settings:salonConfig.trackingModes.manual.description",
    icon: Clock,
    badge: "settings:salonConfig.trackingModes.manual.badge",
  },
];

/**
 * Get the display option for a given enum value
 */
export function getOutcomeTrackingModeOption(
  value: OutcomeTrackingMode
): EnumOption<OutcomeTrackingMode> | undefined {
  return OUTCOME_TRACKING_MODE_OPTIONS.find((option) => option.value === value);
}

/**
 * Validate and normalize enum values from string (for values coming from database)
 */
export function normalizeOutcomeTrackingMode(value: string): OutcomeTrackingMode {
  // Ensure the value matches one of the enum values
  const normalizedValue = value as OutcomeTrackingMode;
  if (Object.values(OutcomeTrackingMode).includes(normalizedValue)) {
    return normalizedValue;
  }

  // Fallback to default if invalid
  console.warn(
    `Invalid OutcomeTrackingMode value: "${value}". Using default: ${OutcomeTrackingMode.MANUAL}`
  );
  return OutcomeTrackingMode.MANUAL;
}

/**
 * Helper functions to create translated versions of enum options
 */
export function getTranslatedOutcomeTrackingModeOptions(
  t: TFunction
): EnumOption<OutcomeTrackingMode>[] {
  return OUTCOME_TRACKING_MODE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
    description: t(option.description),
    badge: t(option.badge),
  }));
}
